import { computed, ComputedRef, onBeforeUnmount, onMounted, ref, Ref } from 'vue'

export type DocumentFullscreenMode = 'none' | 'native' | 'css'

export type UseDocumentFullscreenParams = {
    contentRef: Ref<HTMLElement | undefined>
    panelRef: Ref<HTMLElement | undefined>
    isFullscreen: Ref<boolean>
}

export type UseDocumentFullscreenResult = {
    toggleFullscreen: () => Promise<void>
    exitFullscreen: () => Promise<void>
    isCssFullscreen: ComputedRef<boolean>
    onPanelPointerDown: (e: PointerEvent) => void
    onPanelPointerUp: (e: PointerEvent) => void
    onPanelPointerCancel: (e: PointerEvent) => void
}

const SWIPE_EXIT_DY_PX = 80
const SWIPE_MAX_DX_PX = 40

const supportsNativeFullscreen = (el: HTMLElement | undefined): boolean => {
  if (typeof document === 'undefined' || !el) return false
  if (!document.fullscreenEnabled) return false
  return typeof el.requestFullscreen === 'function'
}

export const useDocumentFullscreen  = (
  params: UseDocumentFullscreenParams,
): UseDocumentFullscreenResult => {
  const { contentRef, isFullscreen } = params


  const mode = ref<DocumentFullscreenMode>('none')
  const isCssFullscreen = computed(() => mode.value === 'css')

  let swipeStartX = 0
  let swipeStartY = 0
  let swipeStartScrollTop = 0
  let swipeTracking = false

  const enterCssFullscreen = () => {
    mode.value = 'css'
    isFullscreen.value = true
  }

  const exitFullscreen = async () => {
    if (mode.value === 'native' || document.fullscreenElement) {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      }
      mode.value = 'none'
      isFullscreen.value = false
      return
    }
    if (mode.value === 'css') {
      mode.value = 'none'
      isFullscreen.value = false
    }
  }

  const toggleFullscreen = async () => {
    if (mode.value === 'css' || document.fullscreenElement) {
      await exitFullscreen()
      return
    }

    const el = contentRef.value
    if (supportsNativeFullscreen(el)) {
      try {
        await el!.requestFullscreen()
        mode.value = 'native'
        isFullscreen.value = true
        return
      } catch {
        enterCssFullscreen()
        return
      }
    }

    enterCssFullscreen()
  }

  const onFullscreenChange = () => {
    if (document.fullscreenElement) {
      mode.value = 'native'
      isFullscreen.value = true
      return
    }

    if (mode.value === 'native') {
      mode.value = 'none'
      isFullscreen.value = false
    }
  }

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    if (mode.value !== 'css') return
    void exitFullscreen()
  }

  const resetSwipe = () => {
    swipeTracking = false
  }

  const onPanelPointerDown = (e: PointerEvent) => {
    if (mode.value !== 'css') return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    swipeTracking = true
    swipeStartX = e.clientX
    swipeStartY = e.clientY
    swipeStartScrollTop = contentRef.value?.scrollTop ?? 0
  }

  const onPanelPointerUp = (e: PointerEvent) => {
    if (!swipeTracking || mode.value !== 'css') {
      resetSwipe()
      return
    }
    const dy = e.clientY - swipeStartY
    const dx = Math.abs(e.clientX - swipeStartX)
    const canDismiss = swipeStartScrollTop === 0 && dy > SWIPE_EXIT_DY_PX && dx < SWIPE_MAX_DX_PX
    resetSwipe()
    if (canDismiss) void exitFullscreen()
  }

  const onPanelPointerCancel = () => {
    resetSwipe()
  }

  onMounted(() => {
    document.addEventListener('fullscreenchange', onFullscreenChange)
    window.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    window.removeEventListener('keydown', onKeydown)
    if (mode.value === 'css') {
      mode.value = 'none'
      isFullscreen.value = false
    }
  })

  return {
    exitFullscreen,
    toggleFullscreen,
    isCssFullscreen,
    onPanelPointerDown,
    onPanelPointerUp,
    onPanelPointerCancel,
  }
}
